// Traduz a atividade do AVA (payload de singleViewByExecution) para o modelo
// neutro que o núcleo da trilha consome (contrato em ../README.md). Função
// pura: recebe dados, devolve dados.
//
// O objeto original do item vai em `fase.referencia`, intacto — é ele que o
// `openItem` da página Do consome para decidir o que abrir (modal, iframe,
// download, avaliação...). Nem o núcleo nem esta função interpretam tipo de
// item; `referencia` é opaca de propósito, e é o que mantém o núcleo portável.

// Pseudo-itens de gravação de aula: o backend concatena o resultado de
// `Room_recording::listAsItemWithExecution` na lista de itens, com `NULL as id`,
// `NULL as order`, `0 as item_percentage` e o nome fixo "Gravação da aula".
// A lista clássica do AVA já os esconde (className "d-none" em Widgets.js);
// na trilha eles seriam nós fantasma, impossíveis de concluir (sem id não há
// como registrar execução).
const NOME_GRAVACAO = "Gravação da aula";

/* De que FAMÍLIA é cada tipo de conteúdo do AVA — a tradução de `type_id` para o
   campo `tipo` do modelo neutro, e a única tabela do módulo que conhece os ids do
   host. Ela mora aqui, e não no núcleo, porque é exatamente o tipo de
   conhecimento que a camada `ava/` existe para absorver: o núcleo recebe
   "exercicio" e desenha um "?", sem saber que no AVA isso é `type_id` 12.

   A LISTA É O QUE O PROFESSOR PODE ESCOLHER, e não o que já foi usado. Esse foi o
   critério errado da primeira versão: eu tinha medido os itens das trilhas
   existentes em `ava_homologa` (Aula 89,1%, Avaliação 10,2%, e 0,7% de resto) e
   coberto quatro famílias. Mas a tela de criação oferece ONZE tipos — o Kilson
   mandou o print —, e `content_types` tem DEZENOVE com `activity = true`. O que
   ninguém usou ainda é exatamente o que aparece amanhã, e um nó sem pictograma
   num tipo oferecido na tela é uma lacuna, não uma decisão.

   Os 19 (conferidos no banco em 21/08/2026; a tela mostra 11 porque o
   `ActivityCreator` filtra por `user_modules` da escola):

     Animação 4      Arquivo 17    Áudio 11        Aula 1        Avaliação 12
     Bandeiras 7     Biblioteca 9  Editor 33       Imagem 10     Jogo 2
     Link 16         Literarte 14  Mapa 5          Modelo 3D 8   Newton 15
     Pontuando 24    Simuladores 6 Upload 18       Vídeo 3

   O AGRUPAMENTO É POR VERBO, não por tecnologia: o que muda para a criança é o que
   ela vai fazer. Por isso Vídeo e Animação são a mesma família (assistir), e
   Simuladores, Bandeiras, Newton e Modelo 3D também (experimentar) — no AVA esses
   quatro são `rocket`, `rocket`, `lightbulb-o` e `cube`, três desenhos para um
   verbo só.

   O que NÃO está aqui cai em `null`, e o nó fica com a estrela genérica. Continua
   valendo para o que sobra (Perfil, Sala, Tutorial, Plano de Aula — nada disso é
   conteúdo de fase) e para o tipo que o host inventar depois: os tipos vêm do
   BANCO, não de uma constante do frontend, e errar o pictograma é pior que não
   ter, porque mente para a criança sem ninguém notar. */
const FAMILIA_POR_TIPO = {
  // Assistir
  3: "video", // Vídeo
  4: "video", // Animação
  23: "video", // Gravação
  26: "video", // Vídeo do Youtube
  30: "video", // Tutorial - Vídeo
  // Ouvir
  11: "audio", // Áudio
  // Ler
  9: "leitura", // Biblioteca Virtual
  13: "leitura", // Página
  17: "leitura", // Arquivo
  27: "leitura", // Arquivo do Google Drive
  32: "leitura", // Página Gerador
  // A aula do AVA, que é o container de conteúdo e não um verbo
  1: "aula", // Aula
  31: "aula", // Aula Gerador
  // Responder
  12: "exercicio", // Avaliação (o professor lê "Exercício" na tela)
  19: "exercicio", // Atividade
  25: "exercicio", // Questão
  28: "exercicio", // Formulário do Google Forms
  // Ver
  10: "imagem", // Imagem
  5: "mapa", // Mapa
  // Jogar
  2: "jogo", // Jogo
  // Experimentar
  6: "explorar", // Simuladores
  7: "explorar", // Bandeiras
  8: "explorar", // Modelo 3D
  15: "explorar", // Newton
  22: "explorar", // Dourado
  // Escrever
  14: "escrita", // Literarte
  33: "escrita", // Editor
  24: "escrita", // Pontuando
  // Enviar
  18: "envio", // Upload
  // Sair do AVA
  16: "link" // Link
};

/**
 * A família visual de um item, para o núcleo escolher o pictograma do nó.
 *
 * @param {number|string} tipoId `type_id` do item
 * @returns {string|null} uma das 12 famílias, ou `null` para tipo desconhecido
 */
export function familiaDoTipo(tipoId) {
  return FAMILIA_POR_TIPO[Number(tipoId)] || null;
}

export function ehItemDeTrilha(item) {
  if (!item) return false;
  if (item.id === null || item.id === undefined) return false;
  return item.name !== NOME_GRAVACAO;
}

/**
 * `activity_items` tem coluna `order` desde a migration original, mas
 * `Item::filter` (Backend) não aplica `orderBy` — a ordem vem do que o banco
 * devolver. Para uma trilha sequencial isso não pode ficar ao acaso, então a
 * ordenação é garantida aqui, com `id` como desempate e itens sem `order` no
 * fim (é o que acontece com registros legados).
 */
export function compararPorOrdem(a, b) {
  const ordemA = ordemNumerica(a.order);
  const ordemB = ordemNumerica(b.order);
  if (ordemA !== ordemB) return ordemA - ordemB;
  return Number(a.id) - Number(b.id);
}

// `Number(null)` é 0 — usar só Number.isFinite jogaria os itens sem `order`
// para o *início* da trilha em vez do fim.
function ordemNumerica(valor) {
  if (valor === null || valor === undefined || valor === "") return Infinity;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : Infinity;
}

function ehConcluido(item) {
  return Number(item.item_percentage) === 100;
}

/**
 * @param {object} activity atividade de singleViewByExecution (com `items`)
 * @param {number} agora timestamp, injetado para o teste não depender do relógio
 * @returns {{totalFases, sequencial, encerrada, fases}} modelo neutro
 */
export function atividadeParaTrilha(activity, agora = Date.now()) {
  const itens = (activity && activity.items) || [];
  const ordenados = itens.filter(ehItemDeTrilha).sort(compararPorOrdem);

  const fim = activity && activity.activity_end;
  const fimEmMs = fim ? Date.parse(fim) : NaN;

  return {
    totalFases: ordenados.length,
    // I-1: atividade marcada como trilha mas não sequencial continua sendo
    // trilha — só não bloqueia nada.
    sequencial: Boolean(activity && activity.sequential_execution),
    // Prazo vencido. O núcleo não sabe o que é prazo — quem traduz isso em
    // aviso na tela é TrilhaDeAtividade.
    encerrada: Number.isFinite(fimEmMs) && fimEmMs < agora,
    fases: ordenados.map((item, indice) => ({
      numero: indice + 1,
      // Campos do contrato neutro (o núcleo só lê estes três):
      titulo: item.title || item.name || `Fase ${indice + 1}`,
      concluida: ehConcluido(item),
      referencia: item,
      // Extras específicos do AVA, para quem consome o modelo depois do núcleo
      // (estatísticas, tooltips, futura cerimônia de card):
      // Do contrato neutro também, desde 21/08/2026: a família do tipo, que é o
      // que decide o pictograma do nó liberado. Campo de VOCABULÁRIO FECHADO
      // (quatro valores e `null`), e não o `type_id` cru nem o nome do ícone
      // FontAwesome — o núcleo não pode saber o que é `check-square-o`, e a
      // lista clássica do AVA, que usa `fa fa-${type_icon}`, depende de uma
      // fonte do host que o módulo não tem direito de exigir.
      tipo: familiaDoTipo(item.type_id),
      subtitulo: item.subtitle || item.type_name || "",
      tipoId: item.type_id,
      icone: item.type_icon,
      cor: item.type_color,
      nota: item.item_grade === undefined ? null : item.item_grade
    }))
  };
}
